import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveNotification } from '@/features/notifications/resolveNotification';
import { useNotificationPopupData } from '@/features/notifications/useNotificationPopupData';
import NotificationPopupView from '@/UI/Popups/NotificationPopup';

import type { NotificationPopupItem } from '@/UI/Popups/NotificationPopup';

/**
 * Connected notifications bell for the app nav. Resolves domain data here so
 * `@/UI/Popups/NotificationPopup` stays presentational.
 */
const NotificationPopup = () => {
  const [t] = useTranslation();
  const {
    isLoading,
    unreadCount,
    isAdmin,
    isClient,
    isRecruiter,
    locale,
    visibleNotifications,
    scrollRef,
    onScroll,
    markAsRead,
    handleNotificationClick,
  } = useNotificationPopupData();

  const items: NotificationPopupItem[] = useMemo(
    () =>
      visibleNotifications.map((notification) => {
        const resolved = resolveNotification(
          notification,
          isClient,
          { isRecruiter },
          t,
        );
        return {
          id: notification.id,
          notificationType: notification.notification_type,
          createdAt: notification.created_at,
          ...resolved,
        };
      }),
    [isClient, isRecruiter, t, visibleNotifications],
  );

  const onItemClick = useCallback(
    (item: NotificationPopupItem, closePopover: () => void) => {
      handleNotificationClick(item.link, item.disabled, item.id, closePopover);
    },
    [handleNotificationClick],
  );

  return (
    <NotificationPopupView
      unreadCount={unreadCount}
      isLoading={isLoading}
      showMarkAsRead={!!unreadCount && !isAdmin}
      items={items}
      locale={locale}
      scrollRef={scrollRef}
      onScroll={onScroll}
      onMarkAsRead={markAsRead}
      onItemClick={onItemClick}
    />
  );
};

export default NotificationPopup;
